// ============================================================
// SPRINT 2 — Page Route Optimisation
// ------------------------------------------------------------
// File d'attente des demandes d'optimisation de route.
// Les SAC et le Répartiteur peuvent regrouper des commandes
// (via "Envoyer à l'optimisation") puis les traiter ici :
//   - Optimisation automatique (HERE Routing + OR-Tools)
//   - Réordonnancement manuel (drag & drop)
//   - Envoi vers SAC (RDV à planifier) ou Répartiteur (Grille Commandes)
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Route, Inbox, CalendarCheck2, Users, Loader2, Zap, Send, Trash2, History,
  ArrowUp, ArrowDown, MapPin, Clock, Package, Truck, ChevronRight, RotateCcw,
  CheckCircle2, AlertTriangle, Archive, GripVertical, X, Maximize2, Phone, FileText, User,
} from 'lucide-react';
import { toast } from 'sonner';
import RouteMiniMap from '../components/RouteMiniMap';

const TOP_TABS = [
  { href: '/admin/service-client/a-planifier', label: 'RDV à planifier', icon: Inbox, active: false },
  { href: '/admin/route-optimisation',         label: 'Route Optimisation', icon: Route, active: true },
  { href: '/admin/service-client/planifies',   label: 'RDV planifiés',   icon: CalendarCheck2, active: false },
  { href: '/admin/service-client/attribuees',  label: 'Attribuées au répartiteur', icon: Users, active: false },
];

const RouteOptimisationPage = () => {
  const { get, post, patch, del } = useAdminApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectId = searchParams.get('group');

  const [pendingGroups, setPendingGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(preselectId || null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // 'pending' | 'history'
  const [optimizing, setOptimizing] = useState(false);
  const [sending, setSending] = useState(false);
  const [destination, setDestination] = useState('sac'); // 'sac' | 'dispatch'
  const [selectedMode, setSelectedMode] = useState(null); // 'rapide' | 'courte' | 'economique' | null
  const [expandedMapMode, setExpandedMapMode] = useState(null); // option mode pour grande carte
  const [stopDetail, setStopDetail] = useState(null); // arrêt ouvert en modal
  // Sprint 2 — Type de véhicule
  const [vehiclePresets, setVehiclePresets] = useState([]);
  const [vehiclePresetId, setVehiclePresetId] = useState('semi_53pi_6_essieux');
  const [stackable, setStackable] = useState(false);
  const [palletSizeIn, setPalletSizeIn] = useState(48); // 48 (léger) ou 44 (lourd QC)
  const [fullLoadThreshold, setFullLoadThreshold] = useState(0.9);
  const [departureAddress, setDepartureAddress] = useState('');

  // ------------------ Fetch ------------------
  const fetchPending = useCallback(async () => {
    const res = await get('/dispatch/route-opt/pending');
    if (res.success) setPendingGroups(res.data.groups || []);
  }, [get]);

  const fetchHistory = useCallback(async () => {
    const res = await get('/dispatch/route-opt/history');
    if (res.success) setHistory(res.data.groups || []);
  }, [get]);

  const fetchDetail = useCallback(async (groupId) => {
    if (!groupId) { setGroupDetail(null); return; }
    const res = await get(`/dispatch/route-opt/${groupId}`);
    if (res.success) {
      setGroupDetail(res.data.group);
      setSelectedMode(res.data.group.selected_mode || null);
    }
  }, [get]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchPending(), fetchHistory()]);
      // Charger la liste des véhicules SAAQ 2026
      const vres = await get('/dispatch/route-opt/vehicle-presets/list');
      if (vres.success) {
        setVehiclePresets(vres.data.presets || []);
        if (vres.data.default_id) setVehiclePresetId(vres.data.default_id);
      }
      setLoading(false);
    })();
  }, [fetchPending, fetchHistory, get]);

  useEffect(() => {
    fetchDetail(selectedGroupId);
  }, [selectedGroupId, fetchDetail]);

  // Si preselect depuis url + groupe pas visible, sélectionne-le
  useEffect(() => {
    if (preselectId) {
      setSelectedGroupId(preselectId);
      // nettoyer l'URL
      searchParams.delete('group');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectId]);

  // ------------------ Actions ------------------
  const moveStop = (fromIdx, toIdx) => {
    if (!groupDetail?.stops) return;
    const next = [...groupDetail.stops];
    const [removed] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, removed);
    setGroupDetail({ ...groupDetail, stops: next });
    saveStopsOrder(next.map((s) => s.id));
  };

  const saveStopsOrder = async (stopIds) => {
    if (!groupDetail?.id) return;
    const res = await patch(`/dispatch/route-opt/${groupDetail.id}/stops`, { stop_ids: stopIds });
    if (!res.success) {
      toast.error(res.error || "Erreur sauvegarde ordre");
    } else {
      setSelectedMode(null); // ordre manuel actif
    }
  };

  const handleOptimize = async (presetIdOverride = null) => {
    if (!groupDetail) return;
    // Protection : si appelé depuis onClick natif, le 1er arg est un SyntheticEvent
    // → on l'ignore et on utilise le preset courant
    const safeOverride = (typeof presetIdOverride === 'string') ? presetIdOverride : null;
    const presetToUse = safeOverride || vehiclePresetId;
    // Si override valide, mettre à jour l'état sélecteur pour cohérence UI
    if (safeOverride && safeOverride !== vehiclePresetId) {
      setVehiclePresetId(safeOverride);
    }
    setOptimizing(true);
    const res = await post(`/dispatch/route-opt/${groupDetail.id}/optimize`, {
      vehicle_preset_id: presetToUse,
      stackable,
      pallet_size_in: palletSizeIn,
      full_load_threshold: fullLoadThreshold,
      departure_address: departureAddress || null,
    });
    setOptimizing(false);
    if (res.success) {
      toast.success(`${res.data.options.length} options optimisées calculées`);
      // Sprint 2.7 : afficher les warnings (ex: capacité dépassée)
      const warnings = res.data.warnings || [];
      warnings.forEach((w) => toast.error(w, { duration: 8000 }));
      await fetchDetail(groupDetail.id);
      await fetchPending();
      // Par défaut, sélectionner le mode "rapide"
      if (res.data.options.length > 0) setSelectedMode(res.data.options[0].mode);
    } else {
      toast.error(res.error || 'Erreur optimisation');
    }
  };

  // Sprint 2.7.1 — Appliquer l'option B (changer de véhicule + relancer)
  const handleApplyUpgrade = async (newPresetId) => {
    if (!newPresetId || !groupDetail) return;
    toast.info(`Changement de véhicule → relance de l'optimisation…`);
    await handleOptimize(newPresetId);
  };

  const handleSend = async () => {
    if (!groupDetail) return;
    if (!window.confirm(`Envoyer cette route (${groupDetail.stops.length} arrêts) vers ${destination === 'sac' ? 'RDV à planifier (SAC)' : 'Grille Commandes (Répartiteur)'} ?`)) return;
    setSending(true);
    const res = await post(`/dispatch/route-opt/${groupDetail.id}/send`, {
      destination,
      selected_mode: selectedMode,
    });
    setSending(false);
    if (res.success) {
      toast.success(`Route envoyée : nouvelle commande ${res.data.new_order_number}`);
      setSelectedGroupId(null);
      await Promise.all([fetchPending(), fetchHistory()]);
      // Rediriger vers la page de destination
      if (destination === 'sac') {
        navigate('/admin/service-client/a-planifier');
      } else {
        navigate('/admin/commandes');
      }
    } else {
      toast.error(res.error || "Erreur lors de l'envoi");
    }
  };

  const handleCancel = async () => {
    if (!groupDetail) return;
    if (!window.confirm("Annuler ce regroupement ? Les commandes d'origine seront restaurées.")) return;
    const res = await del(`/dispatch/route-opt/${groupDetail.id}`);
    if (res.success) {
      toast.success('Regroupement annulé, commandes restaurées');
      setSelectedGroupId(null);
      await Promise.all([fetchPending(), fetchHistory()]);
    } else {
      toast.error(res.error || 'Erreur annulation');
    }
  };

  // ─── Sprint 2.7.4 — Actions sur les alertes de surcharge ───
  const handleAcceptSplit = async (kind, trips) => {
    if (!groupDetail || !trips?.length) return;
    const kindLabel = kind === 'weight' ? 'POIDS (SAAQ)' : 'palettes';
    if (!window.confirm(`Accepter la répartition en ${trips.length} trajets conformes (${kindLabel}) ?\n\nLe groupe courant sera archivé et ${trips.length} nouveaux groupes seront créés en file.`)) return;
    const res = await post(`/dispatch/route-opt/${groupDetail.id}/accept-split`, { kind, trips });
    if (res.success) {
      toast.success(`${res.data.count} trajets conformes créés en file d'attente.`);
      setSelectedGroupId(null);
      await Promise.all([fetchPending(), fetchHistory()]);
    } else {
      toast.error(res.error || 'Erreur lors du split');
    }
  };

  const handleReturnToDispatcher = async () => {
    if (!groupDetail?.stops?.length) return;
    // Pour simplifier V1 : on retourne TOUTES les commandes du groupe au répartiteur
    const stopIds = groupDetail.stops.map((s) => s.id);
    const sourceCount = new Set(groupDetail.stops.map((s) => s.source_order_id).filter(Boolean)).size;
    if (!window.confirm(`Retourner les ${sourceCount} commande(s) au répartiteur (Grille Commandes) ?\nElles pourront y être jumelées avec d'autres cueillettes.`)) return;
    const res = await post(`/dispatch/route-opt/${groupDetail.id}/return-to-dispatcher`, {
      stop_ids: stopIds,
      reason: 'Excédent de capacité — jumelage souhaité par le dispatcher',
    });
    if (res.success) {
      toast.success(`${res.data.returned_count} commande(s) renvoyée(s) au répartiteur.`);
      setSelectedGroupId(null);
      await Promise.all([fetchPending(), fetchHistory()]);
    } else {
      toast.error(res.error || 'Erreur lors du retour');
    }
  };

  const handleSplitOrder = async (proposal) => {
    if (!groupDetail || !proposal) return;
    // La proposal vient de weight_alternatives.option_c_split.split_proposal
    // Il faut retrouver le source_order_id à partir du pickup_stop_id
    const pickup = (groupDetail.stops || []).find((s) => s.id === proposal.pickup_stop_id);
    const orderId = pickup?.source_order_id;
    if (!orderId) {
      toast.error('Commande source introuvable pour cette scission');
      return;
    }
    if (!window.confirm(
      `Retirer ${proposal.orphan_pallets || 0} palette(s) / ${(proposal.orphan_weight_kg || 0).toLocaleString('fr-CA')} kg ?\n\n` +
      `• Reste sur ce trajet : ${proposal.main_pallets || 0} pal · ${(proposal.main_weight_kg || 0).toLocaleString('fr-CA')} kg\n` +
      `• Surplus : nouvelle commande renvoyée au répartiteur\n\n` +
      `Cueillette et livraison sont mises à jour ensemble. Relancez ensuite l'optimisation.`
    )) return;
    const res = await post(`/dispatch/route-opt/${groupDetail.id}/split-order`, {
      order_id: orderId,
      main_weight_kg: proposal.main_weight_kg,
      main_pallets: proposal.main_pallets,
      orphan_weight_kg: proposal.orphan_weight_kg,
      orphan_pallets: proposal.orphan_pallets,
    });
    if (res.success) {
      toast.success(`Commande scindée. Orpheline ${res.data.orphan_order_number} au répartiteur.`);
      await fetchDetail(groupDetail.id);
      await fetchPending();
    } else {
      toast.error(res.error || 'Erreur scission');
    }
  };

  const handleAcceptOverflow = async () => {
    if (!groupDetail) return;
    const msg = groupDetail.overflow?.weight
      ? "Accepter tel quel ?\n\nATTENTION : le poids dépasse la limite légale SAAQ."
      : 'Accepter le dépassement de palettes tel quel ?';
    if (!window.confirm(msg)) return;
    const res = await post(`/dispatch/route-opt/${groupDetail.id}/accept-overflow`, {});
    if (res.success) {
      toast.success('Dépassement accepté.');
      await fetchDetail(groupDetail.id);
    } else {
      toast.error(res.error || 'Erreur');
    }
  };

  const handleEditOrder = (orderStopId) => {
    // orderStopId = pickup stop id. On navigue vers la fiche commande.
    const pickup = (groupDetail?.stops || []).find((s) => s.id === orderStopId);
    const orderId = pickup?.source_order_id;
    if (!orderId) {
      toast.error('Commande source introuvable');
      return;
    }
    navigate(`/admin/commandes?open=${orderId}`);
  };

  const handleFocusVehicle = (presetId) => {
    setVehiclePresetId(presetId);
    toast.info("Véhicule sélectionné. Cliquez sur 'Relancer l'optimisation' pour recalculer.");
  };

  const currentList = tab === 'pending' ? pendingGroups : history;

  return (
    <div className="h-full min-h-screen bg-slate-950 text-white flex flex-col font-sans" data-testid="route-optimisation-page">
      <TopHeader onNavigate={navigate} />

      <div className="flex-1 flex overflow-hidden">
        {/* === Panneau gauche : liste des groupes === */}
        <aside className="w-80 bg-slate-900/95 border-r border-slate-800 flex flex-col shrink-0 overflow-hidden">
          <div className="flex items-center bg-slate-950 border-b border-slate-800 p-1 mx-3 mt-3 rounded-lg">
            <button
              onClick={() => setTab('pending')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === 'pending' ? 'bg-purple-500 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="tab-pending"
            >
              <Zap size={13} /> En attente ({pendingGroups.length})
            </button>
            <button
              onClick={() => setTab('history')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === 'history' ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'}`}
              data-testid="tab-history"
            >
              <History size={13} /> Historique
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center h-32 text-slate-500">
                <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
              </div>
            ) : currentList.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500 gap-2 text-center">
                <Route size={32} className="opacity-30" />
                <p className="text-xs">
                  {tab === 'pending'
                    ? "Aucune route à optimiser."
                    : "Aucune route dans l'historique."}
                </p>
                {tab === 'pending' && (
                  <p className="text-[10px] text-slate-600 mt-2">
                    Depuis SAC ou la Grille Commandes, cochez plusieurs commandes puis cliquez sur "Envoyer à l'optimisation".
                  </p>
                )}
              </div>
            ) : (
              currentList.map((g) => (
                <GroupCard
                  key={g.id}
                  group={g}
                  active={g.id === selectedGroupId}
                  onClick={() => setSelectedGroupId(g.id)}
                />
              ))
            )}
          </div>
        </aside>

        {/* === Panneau droit : détail du groupe === */}
        <main className="flex-1 overflow-y-auto bg-slate-950 min-w-0">
          {!groupDetail ? (
            <EmptyState />
          ) : (
            <GroupDetail
              group={groupDetail}
              optimizing={optimizing}
              sending={sending}
              destination={destination}
              onChangeDestination={setDestination}
              selectedMode={selectedMode}
              onChangeSelectedMode={setSelectedMode}
              onOptimize={handleOptimize}
              onSend={handleSend}
              onCancel={handleCancel}
              onMoveStop={moveStop}
              isHistory={tab === 'history'}
              setExpandedMapMode={setExpandedMapMode}
              setStopDetail={setStopDetail}
              vehiclePresets={vehiclePresets}
              vehiclePresetId={vehiclePresetId}
              onChangeVehiclePresetId={setVehiclePresetId}
              stackable={stackable}
              onChangeStackable={setStackable}
              palletSizeIn={palletSizeIn}
              onChangePalletSizeIn={setPalletSizeIn}
              fullLoadThreshold={fullLoadThreshold}
              onChangeFullLoadThreshold={setFullLoadThreshold}
              departureAddress={departureAddress}
              onChangeDepartureAddress={setDepartureAddress}
              onAcceptSplit={handleAcceptSplit}
              onReturnToDispatcher={handleReturnToDispatcher}
              onSplitOrder={handleSplitOrder}
              onEditOrder={handleEditOrder}
              onFocusVehicle={handleFocusVehicle}
              onAcceptOverflow={handleAcceptOverflow}
            />
          )}
        </main>
      </div>

      {/* Modal : grande carte d'une option */}
      {expandedMapMode && groupDetail?.optimized_options && (
        <ExpandedMapModal
          option={groupDetail.optimized_options.find((o) => o.mode === expandedMapMode)}
          onClose={() => setExpandedMapMode(null)}
        />
      )}

      {/* Modal : détail d'un arrêt */}
      {stopDetail && (
        <StopDetailModal
          stop={stopDetail}
          onClose={() => setStopDetail(null)}
        />
      )}
    </div>
  );
};

// ============================================================
// TOP HEADER
// ============================================================
const TopHeader = ({ onNavigate }) => (
  <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30">
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-purple-500/15 border border-purple-500/40 flex items-center justify-center text-purple-300 font-bold">
          <Route size={18} />
        </div>
        <div>
          <h1 className="text-lg font-bold uppercase tracking-wide text-white leading-tight">Route Optimisation</h1>
          <p className="text-xs text-slate-400">Regrouper les arrêts • Calculer la meilleure séquence • Envoyer au SAC ou au Répartiteur</p>
        </div>
      </div>
      <div className="flex items-center bg-slate-950 border border-slate-800 p-1 rounded-lg ml-2 flex-wrap">
        {TOP_TABS.map((t) => (
          <button
            key={t.href}
            onClick={() => onNavigate(t.href)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              t.active ? 'bg-amber-500 text-black shadow-sm' : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            data-testid={`subtab-${t.href}`}
          >
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// GROUP CARD (liste de gauche)
// ============================================================
const GroupCard = ({ group, active, onClick }) => {
  const statusMeta = {
    pending: { label: '⏳ En attente', class: 'bg-slate-700/70 text-slate-200' },
    optimized: { label: '✓ Optimisé', class: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' },
    sent: { label: '📤 Envoyé', class: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
    archived: { label: '✗ Annulé', class: 'bg-slate-800 text-slate-500' },
  };
  const meta = statusMeta[group.status] || statusMeta.pending;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-colors ${
        active
          ? 'bg-purple-500/15 border-purple-500/50 text-white'
          : 'bg-slate-800/40 border-slate-800 hover:border-slate-700 text-slate-300'
      }`}
      data-testid={`group-card-${group.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-bold truncate">{group.name}</span>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${meta.class}`}>{meta.label}</span>
      </div>
      <div className="text-[11px] text-slate-500 space-y-0.5">
        <div className="flex items-center gap-1.5">
          <Package size={11} />
          <span>{group.source_order_ids?.length || 0} cmd • {group.stops?.length || 0} arrêts</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={11} />
          <span>{new Date(group.created_at).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })}</span>
        </div>
        <div className="text-[10px] text-slate-600">Par {group.created_by_name || '—'}</div>
      </div>
    </button>
  );
};

// ============================================================
// EMPTY STATE
// ============================================================
const EmptyState = () => (
  <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-3 p-8 text-center">
    <Route size={64} className="opacity-20" />
    <p className="text-base font-semibold text-slate-400">Aucune route sélectionnée</p>
    <p className="text-xs max-w-md">
      Choisissez une route dans la liste à gauche, ou allez dans <strong className="text-slate-300">SAC</strong> / <strong className="text-slate-300">Grille Commandes</strong> et cliquez sur <strong className="text-purple-400">Envoyer à l'optimisation</strong> après avoir sélectionné plusieurs commandes.
    </p>
  </div>
);

// ============================================================
// SPRINT 2.7.4 — ALERTES SURCHARGE (POIDS + PALETTES)
// ============================================================
const fmtKg = (v) => (v ?? 0).toLocaleString('fr-CA');

const OverflowOptionButton = ({ testId, tone, title, children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`text-left bg-slate-900/60 border border-slate-700 rounded-lg p-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tone}`}
    data-testid={testId}
  >
    <div className="text-[11px] font-bold mb-1">{title}</div>
    <div className="text-[10px] text-slate-300 leading-relaxed">{children}</div>
  </button>
);

const OverflowAlert = ({ group, onAcceptSplit, onSplitOrder, onAcceptOverflow }) => {
  const ov = group?.overflow;
  if (!ov) return null;
  if (group.overflow_accepted) {
    return (
      <div className="mb-4 bg-emerald-500/10 border border-emerald-500/40 rounded-lg px-4 py-3 text-[12px] text-emerald-200" data-testid="overflow-accepted-banner">
        Dépassement accepté tel quel par {group.overflow_accepted_by || 'le répartiteur'}
        {ov.weight && <strong className="text-red-300"> — attention : poids non conforme SAAQ</strong>}
      </div>
    );
  }
  const { accept_as_is: accept, remove_pallets: remove, split_two_trips: split } = ov.options || {};
  const isWeight = !!ov.weight;
  return (
    <div
      className={`mb-4 rounded-lg p-4 border-2 ${isWeight ? 'bg-red-500/10 border-red-500/60' : 'bg-amber-500/10 border-amber-500/60'}`}
      data-testid={isWeight ? 'weight-overflow-alert' : 'pallets-overflow-alert'}
    >
      <h4 className={`text-sm font-bold uppercase tracking-wide ${isWeight ? 'text-red-300' : 'text-amber-300'}`}>
        {isWeight ? 'Surcharge POIDS — Loi SAAQ' : 'Excédent palettes'}
      </h4>
      <div className="text-[12px] text-slate-200 mt-1 space-y-0.5" data-testid="overflow-summary">
        {ov.weight && (
          <p>Poids à bord : <strong>{fmtKg(ov.weight.peak)} kg</strong> · Permis : <strong>{fmtKg(ov.weight.capacity)} kg</strong> · <strong className="text-red-300">+{fmtKg(ov.weight.excess)} kg</strong></p>
        )}
        {ov.pallets && (
          <p>Palettes à bord : <strong>{ov.pallets.peak}</strong> · Capacité : <strong>{ov.pallets.capacity}</strong> · <strong className="text-amber-300">+{ov.pallets.excess} pal</strong></p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
        <OverflowOptionButton testId="overflow-option-accept" tone="hover:border-emerald-400 text-emerald-300" title="1 · Accepter tel quel" onClick={onAcceptOverflow} disabled={!accept?.available}>
          Continuer avec le chargement actuel.
          {accept?.warning && <span className="block text-red-300 mt-0.5">{accept.warning}</span>}
        </OverflowOptionButton>
        <OverflowOptionButton testId="overflow-option-remove" tone="hover:border-amber-400 text-amber-300" title="2 · Retirer des palettes" onClick={() => onSplitOrder?.(remove)} disabled={!remove || (!remove.remove_pallets && !remove.remove_weight_kg)}>
          Retirer <strong>{remove?.remove_pallets || 0} pal</strong>{remove?.remove_weight_kg ? <> / <strong>{fmtKg(remove.remove_weight_kg)} kg</strong></> : null} de la plus grosse commande.
          Le surplus retourne au répartiteur.
          {remove && !remove.sufficient && <span className="block text-red-300 mt-0.5">Insuffisant à lui seul</span>}
        </OverflowOptionButton>
        <OverflowOptionButton testId="overflow-option-split" tone="hover:border-blue-400 text-blue-300" title="3 · Séparer en 2 trajets" onClick={() => onAcceptSplit?.(isWeight ? 'weight' : 'pallets', split?.trips)} disabled={!split?.feasible}>
          {split?.feasible ? (
            <>
              {split.trips.map((t, i) => (
                <span key={i} className="block" data-testid={`overflow-split-trip-${i}`}>
                  {t.label} : {t.total_pallets} pal · {fmtKg(t.total_weight_kg)} kg
                </span>
              ))}
              <span className="block text-slate-500 mt-0.5">Groupes d'adresses identiques conservés.</span>
            </>
          ) : (
            <span className="text-slate-400">{split?.reason || 'Séparation impossible'}</span>
          )}
        </OverflowOptionButton>
      </div>
    </div>
  );
};

const RULE_LABELS = [
  ['grouping', '1 Groupage'], ['capacity', '2 Capacité'], ['overflow', '3 Dépassement'],
  ['lifo', '4 LIFO'], ['full_load', '5 Full load ≥24'], ['geo', '6 HERE camion'], ['rdv', '7 RDV'],
];

const RulesReport = ({ group }) => {
  const r = group?.rules_report;
  if (!r) return null;
  const ov = group.overflow;
  const state = {
    grouping: true,
    capacity: !ov,
    overflow: !ov || group.overflow_accepted,
    lifo: r.lifo_ok,
    full_load: r.full_load_ok,
    geo: true,
    rdv: r.rdv_ok,
  };
  return (
    <div className="mb-3 flex flex-wrap gap-1.5" data-testid="rules-report">
      {RULE_LABELS.map(([k, label]) => (
        <span
          key={k}
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${state[k] ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-red-500/50 text-red-300 bg-red-500/10'}`}
          data-testid={`rule-chip-${k}`}
        >
          {state[k] ? '✓' : '✗'} {label}
        </span>
      ))}
      <span className="text-[10px] text-slate-500 self-center" data-testid="rules-nodes-count">
        {r.stops_count} arrêts → {r.nodes_count} arrêts physiques
      </span>
    </div>
  );
};


// ============================================================
// GROUP DETAIL
// ============================================================
const GroupDetail = ({
  group, optimizing, sending, destination, onChangeDestination, selectedMode, onChangeSelectedMode,
  onOptimize, onSend, onCancel, onMoveStop, isHistory, setExpandedMapMode, setStopDetail,
  vehiclePresets, vehiclePresetId, onChangeVehiclePresetId, stackable, onChangeStackable,
  palletSizeIn, onChangePalletSizeIn,
  fullLoadThreshold, onChangeFullLoadThreshold, departureAddress, onChangeDepartureAddress,
  onAcceptSplit, onReturnToDispatcher, onSplitOrder, onEditOrder, onFocusVehicle, onAcceptOverflow,
}) => {
  const options = group.optimized_options || [];
  const warnings = group.optimization_warnings || [];
  const chosenOption = useMemo(
    () => (selectedMode ? options.find((o) => o.mode === selectedMode) : null),
    [options, selectedMode]
  );

  // Si un mode est choisi, on affiche l'ordre venant du mode, sinon ordre courant
  const displayStops = useMemo(() => {
    if (chosenOption?.stops_ordered?.length) {
      const map = Object.fromEntries((group.stops || []).map((s) => [s.id, s]));
      return chosenOption.stops_ordered.map((so) => ({ ...map[so.id], _arrival: so.arrival_estimate, _rdv_ok: so.rdv_ok })).filter(Boolean);
    }
    return group.stops || [];
  }, [chosenOption, group.stops]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* === Header de l'option sélectionnée === */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">{group.name}</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Origine : {group.source_type === 'sac' ? 'SAC (Service Client)' : 'Répartition'} • {group.source_order_ids?.length} commandes regroupées
            </p>
            {group.resulting_order_number && (
              <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1.5">
                <CheckCircle2 size={13} />
                Envoyée vers {group.sent_to === 'sac' ? 'SAC — RDV à planifier' : 'Grille Commandes'} sous la commande <strong>{group.resulting_order_number}</strong>
              </p>
            )}
          </div>
          {!isHistory && (
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                className="px-3 py-2 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-red-500/20 hover:text-red-300 border border-slate-700 text-slate-300 flex items-center gap-1.5"
                data-testid="cancel-group-btn"
              >
                <Trash2 size={13} /> Abandonner
              </button>
            </div>
          )}
        </div>

        {/* === Options d'optimisation === */}
        {!isHistory && (
          <>
            {/* Sprint 2.7.4 — Alertes de surcharge POIDS/PALETTES avec 3 options actionnables */}
            <RulesReport group={group} />
            <OverflowAlert
              group={group}
              onAcceptSplit={onAcceptSplit}
              onSplitOrder={onSplitOrder}
              onAcceptOverflow={onAcceptOverflow}
            />

            {/* Sprint 2.7 — Warnings légers (ex: LIFO, RDV) sans bloquer l'écran.
                Le dispatcher est maître du choix du véhicule. */}
            {warnings.length > 0 && (
              <div
                className="mb-3 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2"
                data-testid="optimization-warnings"
              >
                <div className="flex items-start gap-2">
                  <div className="text-amber-400">ℹ️</div>
                  <div className="flex-1 space-y-0.5">
                    {warnings.slice(0, 2).map((w, i) => (
                      <div key={i} className="text-[11px] text-amber-200/80 leading-relaxed">
                        {w}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Configuration véhicule (SAAQ 2026) */}
            <VehicleConfigCard
              presets={vehiclePresets}
              presetId={vehiclePresetId}
              onChangePresetId={onChangeVehiclePresetId}
              stackable={stackable}
              onChangeStackable={onChangeStackable}
              palletSizeIn={palletSizeIn}
              onChangePalletSizeIn={onChangePalletSizeIn}
              fullLoadThreshold={fullLoadThreshold}
              onChangeFullLoadThreshold={onChangeFullLoadThreshold}
              departureAddress={departureAddress}
              onChangeDepartureAddress={onChangeDepartureAddress}
              groupStops={group.stops || []}
            />
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3 mt-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Zap size={14} className="text-amber-400" /> Options d'optimisation
              </h3>
              <button
                onClick={onOptimize}
                disabled={optimizing}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-amber-500 hover:bg-amber-400 text-black min-h-10 flex items-center gap-2 disabled:opacity-50"
                data-testid="optimize-btn"
              >
                {optimizing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {options.length === 0 ? 'Optimiser automatiquement' : 'Relancer l\'optimisation'}
              </button>
            </div>

            {options.length === 0 ? (
              <div className="bg-slate-950/50 border border-dashed border-slate-800 rounded-lg p-4 text-center text-xs text-slate-500">
                Cliquez sur <strong className="text-amber-400">Optimiser automatiquement</strong> pour calculer la meilleure séquence (basée sur HERE Routing camion).
                Vous pouvez aussi réordonner les arrêts manuellement ci-dessous.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {options.map((opt) => (
                  <OptionCard
                    key={opt.mode}
                    option={opt}
                    active={selectedMode === opt.mode}
                    onClick={() => onChangeSelectedMode(opt.mode)}
                    onExpandMap={() => setExpandedMapMode(opt.mode)}
                  />
                ))}
              </div>
            )}
            {selectedMode && (
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-2">
                <span>Pour revenir à l'ordre manuel : </span>
                <button
                  onClick={() => onChangeSelectedMode(null)}
                  className="text-amber-400 hover:text-amber-300 underline"
                  data-testid="reset-manual-order"
                >
                  Désélectionner l'option
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* === Liste des arrêts === */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <MapPin size={14} className="text-emerald-400" /> Arrêts ({displayStops.length})
          </h3>
          {!isHistory && !selectedMode && (
            <span className="text-[10px] text-slate-500">↑↓ pour réordonner manuellement</span>
          )}
        </div>
        {/* Bannière info : RDV passés ignorés */}
        {(() => {
          const todayStr = new Date().toISOString().slice(0, 10);
          const pastCount = (displayStops || []).filter((s) => {
            if (!s.required_date) return false;
            return String(s.required_date).slice(0, 10) < todayStr;
          }).length;
          if (pastCount === 0) return null;
          return (
            <div className="mb-3 bg-slate-800/40 border border-slate-700 rounded-lg px-3 py-2 text-[11px] text-slate-300 flex items-start gap-2" data-testid="past-rdv-banner">
              <span className="text-amber-400 mt-0.5">🕰</span>
              <div>
                <strong className="text-slate-200">{pastCount} arrêt{pastCount > 1 ? 's ont' : ' a'} une date de RDV dans le passé</strong> — l'optimiseur les ignore et trie uniquement par géographie.
                Pensez à mettre à jour les dates si c'est une erreur de saisie.
              </div>
            </div>
          );
        })()}
        <div className="space-y-2">
          {displayStops.map((s, idx) => (
            <StopRow
              key={s.id}
              stop={s}
              index={idx}
              total={displayStops.length}
              editable={!isHistory && !selectedMode}
              onMoveUp={() => onMoveStop(idx, idx - 1)}
              onMoveDown={() => onMoveStop(idx, idx + 1)}
              onOpenDetail={() => setStopDetail?.({ ...s, _merged_info: group?.merged_stops_info?.[s.id] })}
              arrival={s._arrival}
              rdvOk={s._rdv_ok}
              mergedInfo={group?.merged_stops_info?.[s.id]}
            />
          ))}
        </div>
      </div>

      {/* === Barre d'envoi === */}
      {!isHistory && (
        <div className="bg-slate-900 border border-purple-500/30 rounded-xl p-5 sticky bottom-0 z-10 shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-2">
                <Send size={14} className="text-purple-400" /> Destination
              </h3>
              <div className="flex items-center bg-slate-950 border border-slate-800 p-1 rounded-lg">
                <button
                  onClick={() => onChangeDestination('sac')}
                  className={`px-4 py-2 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors ${
                    destination === 'sac' ? 'bg-cyan-500 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                  data-testid="dest-sac-btn"
                >
                  <RotateCcw size={13} /> Retour au SAC (RDV à planifier)
                </button>
                <button
                  onClick={() => onChangeDestination('dispatch')}
                  className={`px-4 py-2 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors ${
                    destination === 'dispatch' ? 'bg-emerald-500 text-black' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                  data-testid="dest-dispatch-btn"
                >
                  <Truck size={13} /> Retour au Répartiteur (Grille Commandes)
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {selectedMode
                  ? <>Ordre : option <strong className="text-amber-400">{selectedMode}</strong></>
                  : <>Ordre : <strong className="text-slate-300">manuel</strong></>
                }
              </p>
            </div>
            <button
              onClick={onSend}
              disabled={sending || displayStops.length === 0}
              className="px-6 py-3 rounded-lg text-sm font-bold bg-purple-500 hover:bg-purple-400 text-white min-h-12 flex items-center gap-2 disabled:opacity-50"
              data-testid="send-group-btn"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Envoyer la route
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// VEHICLE CONFIG CARD (SAAQ 2026)
// ============================================================
const VehicleConfigCard = ({ presets, presetId, onChangePresetId, stackable, onChangeStackable, palletSizeIn = 48, onChangePalletSizeIn, fullLoadThreshold, onChangeFullLoadThreshold, departureAddress, onChangeDepartureAddress, groupStops = [] }) => {
  const selected = presets?.find((p) => p.id === presetId) || null;
  const isFlatbed = selected?.body_type === 'flatbed';
  const canStack = selected?.stackable && !isFlatbed;
  // Delta géométrique pour palettes 44" (identique à la formule backend)
  const palletDelta44 = (boxLengthPi) => {
    if (!boxLengthPi || boxLengthPi <= 0) return 0;
    const rows48 = Math.floor((boxLengthPi * 12) / 48);
    const rows44 = Math.floor((boxLengthPi * 12) / 44);
    return (rows44 - rows48) * 2;
  };
  const baseFloor = selected ? (selected.pallets_floor + (palletSizeIn === 44 ? palletDelta44(selected.box_length_pi) : 0)) : 0;
  const capacity = selected ? (stackable && canStack ? baseFloor * 2 : baseFloor) : null;
  // Sprint 2.7.3 — Payload réel du véhicule (gross - tare)
  const payloadKg = selected && selected.max_gross_kg != null && selected.tare_weight_kg != null
    ? selected.max_gross_kg - selected.tare_weight_kg
    : null;

  // Calcul de la charge totale demandée (sum des pickups : palettes ET poids)
  const pickupStops = (groupStops || [])
    .filter((s) => (s.stop_type || '').toLowerCase() === 'cueillette' || (s.stop_type || '').toLowerCase() === 'pickup');
  const pickupPallets = pickupStops.map((s) => Number(s.total_pieces || 0));
  const pickupWeightsLbs = pickupStops.map((s) => Number(s.weight_lbs || 0));
  const totalPallets = pickupPallets.reduce((a, b) => a + b, 0);
  const totalWeightLbs = pickupWeightsLbs.reduce((a, b) => a + b, 0);
  const totalWeightKg = Math.round(totalWeightLbs * 0.453592);
  const maxSingle = pickupPallets.length > 0 ? Math.max(...pickupPallets) : 0;
  const overCapacity = capacity != null && totalPallets > capacity;
  const singleOverCapacity = capacity != null && maxSingle > capacity;
  const overPayload = payloadKg != null && totalWeightKg > payloadKg;

  // Force stackable=false quand on sélectionne un flatbed
  React.useEffect(() => {
    if (isFlatbed && stackable) onChangeStackable(false);
  }, [isFlatbed, stackable, onChangeStackable]);
  return (
    <div className="bg-slate-950/60 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="vehicle-config-card">
      <div className="flex items-center gap-2 mb-2">
        <Truck size={14} className="text-amber-400" />
        <h4 className="text-xs font-bold text-amber-300 uppercase tracking-wide">Configuration véhicule (SAAQ 2026)</h4>
        {selected && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${isFlatbed ? 'bg-blue-500/15 border-blue-500/40 text-blue-300' : 'bg-slate-700/40 border-slate-600 text-slate-300'}`}>
            {isFlatbed ? '🏗️ Plateau (flatbed)' : '📦 Fourgon fermé'}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="text-[10px] text-slate-500 uppercase tracking-wide">Type de camion</label>
          <select
            value={presetId}
            onChange={(e) => onChangePresetId(e.target.value)}
            className="w-full mt-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-amber-400 focus:outline-none"
            data-testid="vehicle-preset-select"
          >
            <optgroup label="📦 Fourgons fermés / Dry van">
              {(presets || []).filter(p => p.body_type !== 'flatbed').map((p) => {
                const palCount = p.pallets_floor + (palletSizeIn === 44 ? palletDelta44(p.box_length_pi) : 0);
                const payloadDisp = p.max_gross_kg && p.tare_weight_kg ? (p.max_gross_kg - p.tare_weight_kg) : null;
                return (
                  <option key={p.id} value={p.id}>
                    {`${p.label} — ${palCount} pal ${palletSizeIn === 44 ? '44"' : '48"'} · payload ${payloadDisp ? `${payloadDisp.toLocaleString('fr-CA')} kg` : `${p.max_gross_kg} brut`}`}
                  </option>
                );
              })}
            </optgroup>
            <optgroup label="🏗️ Plateaux / Flatbed">
              {(presets || []).filter(p => p.body_type === 'flatbed').map((p) => {
                const palCount = p.pallets_floor + (palletSizeIn === 44 ? palletDelta44(p.box_length_pi) : 0);
                const payloadDisp = p.max_gross_kg && p.tare_weight_kg ? (p.max_gross_kg - p.tare_weight_kg) : null;
                return (
                  <option key={p.id} value={p.id}>
                    {`${p.label} — ${palCount} pal ${palletSizeIn === 44 ? '44"' : '48"'} · payload ${payloadDisp ? `${payloadDisp.toLocaleString('fr-CA')} kg` : `${p.max_gross_kg} brut`}`}
                  </option>
                );
              })}
            </optgroup>
          </select>
          {selected && (
            <p className="text-[10px] text-slate-500 mt-1">
              Config SAAQ : <strong className="text-slate-300">{selected.config_axes}</strong> ·
              {' '}{selected.axles} essieux · {selected.length_m} m · caisse {selected.box_length_pi} pi
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-[10px] text-slate-500 uppercase tracking-wide">Capacité réelle</label>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 text-sm font-bold text-amber-300" data-testid="vehicle-capacity-display">
            {capacity != null ? `${capacity} palettes` : '—'}
            {payloadKg != null && (
              <div className="text-[10px] text-amber-300/70 font-normal mt-0.5">
                Payload : {payloadKg.toLocaleString('fr-CA')} kg
                <span className="text-slate-500 ml-1" title={`Brute SAAQ ${selected?.max_gross_kg?.toLocaleString('fr-CA')} kg − tare ${selected?.tare_weight_kg?.toLocaleString('fr-CA')} kg`}>
                  (brut − tare)
                </span>
              </div>
            )}
          </div>
          {/* Sprint 2.7.3 — Charge totale demandée (palettes + poids) */}
          {(totalPallets > 0 || totalWeightKg > 0) && (
            <div
              className={`rounded px-3 py-1.5 text-[11px] font-semibold border mt-1 ${
                overCapacity || overPayload
                  ? 'bg-red-500/15 border-red-500/50 text-red-300'
                  : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
              }`}
              data-testid="total-demand-display"
            >
              <div className="flex items-center justify-between">
                <span>
                  Demande : {totalPallets > 0 ? `${totalPallets} pal` : ''}
                  {totalPallets > 0 && totalWeightKg > 0 && ' · '}
                  {totalWeightKg > 0 ? `${totalWeightKg.toLocaleString('fr-CA')} kg` : ''}
                </span>
                {(overCapacity || overPayload) && <span className="text-[10px]">⚠️ DÉPASSE</span>}
              </div>
              {pickupPallets.filter(p => p > 0).length > 1 && (
                <div className="text-[10px] opacity-80 mt-0.5">
                  {pickupStops.length} cueillettes ({pickupPallets.join(' + ')} pal)
                </div>
              )}
              {overPayload && (
                <div className="text-[10px] mt-1 text-red-300">
                  🏋️ Poids {totalWeightKg.toLocaleString('fr-CA')} kg &gt; payload {payloadKg.toLocaleString('fr-CA')} kg
                </div>
              )}
              {singleOverCapacity && (
                <div className="text-[10px] mt-1 text-red-300 font-bold">
                  🚫 1 commande seule ({maxSingle} pal) &gt; capacité
                </div>
              )}
            </div>
          )}
          <label className={`flex items-center gap-1.5 text-[11px] mt-1 ${canStack ? 'text-slate-300 cursor-pointer' : 'text-slate-600 cursor-not-allowed'}`}>
            <input
              type="checkbox"
              checked={stackable}
              onChange={(e) => onChangeStackable(e.target.checked)}
              disabled={!canStack}
              className="accent-amber-500"
              data-testid="vehicle-stackable-checkbox"
            />
            <span>Palettes empilables (&lt; 48" haut) {isFlatbed && <em className="text-slate-500">— N/A sur plateau</em>}</span>
          </label>
          {/* Toggle taille palette 44/48 (Patrice : 50/50 matériel lourd/léger) */}
          <div className="flex items-center gap-1.5 text-[11px] mt-1">
            <span className="text-slate-500">Taille palette :</span>
            <button
              type="button"
              onClick={() => onChangePalletSizeIn && onChangePalletSizeIn(48)}
              className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${palletSizeIn === 48 ? 'bg-amber-500 text-black border-amber-400' : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500'}`}
              data-testid="pallet-size-48-btn"
            >
              48"
            </button>
            <button
              type="button"
              onClick={() => onChangePalletSizeIn && onChangePalletSizeIn(44)}
              className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${palletSizeIn === 44 ? 'bg-amber-500 text-black border-amber-400' : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500'}`}
              data-testid="pallet-size-44-btn"
            >
              44"
            </button>
            <span className="text-slate-500 text-[10px] ml-1">
              {palletSizeIn === 44 ? '(matériel lourd std QC)' : '(matériel léger)'}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid="vehicle-full-load-rule">
        <label className="text-slate-500">Full Load :</label>
        <span className="text-slate-400">
          commandes ≥ <strong className="text-amber-300">24 pal</strong> → cueillette suivie immédiatement de sa livraison
        </span>
      </div>
      <div className="mt-2 text-[10px] text-slate-500 italic flex items-start gap-1.5">
        <AlertTriangle size={10} className="shrink-0 mt-0.5 text-amber-500/60" />
        <span>
          <strong className="text-slate-400">LIFO actif</strong> — chargement inverse de l'ordre de livraison.
          {isFlatbed ? ' Flatbed : du bas vers le haut au chargement, arrière→avant / haut→bas au déchargement.' : ' Fourgon : dernier chargé = premier déchargé par la porte arrière.'}
          {' '}
          <strong className="text-slate-400">RDV horaires = indicatifs</strong> (le chauffeur/dispatcher ajuste sur le terrain).
        </span>
      </div>
      {/* ── Point de départ du camion ── */}
      <div className="mt-3 pt-3 border-t border-slate-800">
        <label className="text-[10px] text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
          <MapPin size={11} className="text-emerald-400" />
          Point de départ du camion <span className="text-slate-600">(optionnel)</span>
        </label>
        <input
          type="text"
          value={departureAddress || ''}
          onChange={(e) => onChangeDepartureAddress(e.target.value)}
          placeholder="Ex: 123 rue du Dépôt, Drummondville, QC"
          className="w-full mt-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-emerald-400 focus:outline-none"
          data-testid="departure-address-input"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          {departureAddress
            ? '✓ L\'algo partira de cette adresse (1er arrêt = cueillette la plus proche).'
            : '⚪ Laissé vide = l\'algo choisit lui-même le meilleur point de départ parmi les arrêts.'}
        </p>
      </div>
    </div>
  );
};


// ============================================================
// OPTION CARD
// ============================================================
const OptionCard = ({ option, active, onClick, onExpandMap }) => {
  const modeMeta = {
    rapide:     { color: 'blue',    emoji: '⚡', title: 'Rapide' },
    courte:     { color: 'green',   emoji: '📏', title: 'Distance mini' },
    economique: { color: 'amber',   emoji: '💰', title: 'Économique' },
  };
  const meta = modeMeta[option.mode] || { color: 'slate', emoji: '•', title: option.mode };
  const miniStops = (option.stops_ordered || []).map((so) => ({
    id: so.id, lat: so.lat, lng: so.lng, label: so.label, stop_type: so.type,
  }));
  return (
    <div
      onClick={onClick}
      className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
        active
          ? `bg-${meta.color}-500/15 border-${meta.color}-500/60 text-white`
          : 'bg-slate-950/50 border-slate-800 hover:border-slate-700 text-slate-300'
      }`}
      data-testid={`option-${option.mode}`}
      role="button"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-bold">{meta.emoji} {meta.title}</span>
        {active && <CheckCircle2 size={14} className="text-emerald-400" />}
      </div>
      <div className="text-[11px] space-y-0.5 text-slate-400 mb-2">
        <div>📍 <strong className="text-white">{option.total_distance_km?.toFixed?.(1) ?? option.total_distance_km} km</strong></div>
        <div>⏱️ <strong className="text-white">{Math.round(option.total_duration_min)} min</strong></div>
        {option.fuel_litres != null && (
          <div>⛽ <strong className="text-white">{option.fuel_litres.toFixed(1)} L</strong></div>
        )}
        {option.hos && !option.hos.conforme && (
          <div className="text-amber-400 flex items-center gap-1 mt-1">
            <AlertTriangle size={11} /> HoS non conforme
          </div>
        )}
      </div>
      {/* Mini-map cliquable pour agrandir */}
      <div className="relative" style={{ height: 120 }}>
        <RouteMiniMap
          polylineCoords={option.polyline_coords || []}
          stops={miniStops}
          compact
          onExpand={onExpandMap}
          height="120px"
        />
        <div className="absolute top-1 right-1 bg-slate-900/80 text-white rounded p-1 pointer-events-none">
          <Maximize2 size={11} />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STOP ROW
// ============================================================
const StopRow = ({ stop, index, total, editable, onMoveUp, onMoveDown, onOpenDetail, arrival, rdvOk, mergedInfo }) => {
  const stopType = (stop.stop_type || stop.type || '').toLowerCase();
  const isPickup = stopType === 'cueillette' || stopType === 'pickup';
  // Détection RDV dans le passé — l'algo l'ignore (voir backend route_opt_queue)
  const rdvInPast = (() => {
    if (!stop.required_date) return false;
    try {
      const d = new Date(String(stop.required_date).slice(0, 10) + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    } catch (_) { return false; }
  })();
  return (
    <div
      className="flex items-center gap-3 p-3 bg-slate-950/50 border border-slate-800 rounded-lg hover:border-amber-500/50 cursor-pointer transition-colors"
      onClick={onOpenDetail}
      data-testid={`stop-row-${stop.id}`}
      role="button"
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${isPickup ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50' : 'bg-green-500/20 text-green-300 border border-green-500/50'}`}>
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase ${isPickup ? 'text-orange-400' : 'text-green-400'}`}>
            {isPickup ? '📦 Cueillette' : '🏁 Livraison'}
          </span>
          <span className="text-xs text-slate-500">•</span>
          <span className="text-xs text-slate-400">{stop.source_order_number || stop.order_number}</span>
          {/* Sprint 2.7.5 — Badge "N bons agrégés" si stop fusionné */}
          {mergedInfo && mergedInfo.merged_count > 1 && (
            <>
              <span className="text-xs text-slate-500">•</span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/40 font-bold"
                title={`${mergedInfo.merged_count} bons à ${isPickup ? 'ramasser' : 'livrer'} à cette adresse`}
                data-testid="stop-merged-badge"
              >
                📋 {mergedInfo.merged_count} bons
              </span>
            </>
          )}
          {/* Sprint 2.7.2 — Badge palettes visible (data que l'algo utilise) */}
          {(() => {
            const pal = mergedInfo?.merged_total_pallets ?? stop.total_pieces;
            const wkg = mergedInfo?.merged_total_weight_kg ?? stop.weight_kg;
            if (pal == null && wkg == null) return null;
            return (
              <>
                <span className="text-xs text-slate-500">•</span>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold"
                  title={`${pal || 0} palettes · ${wkg ? wkg.toLocaleString('fr-CA') + ' kg' : ''}`}
                  data-testid="stop-pallets-badge"
                >
                  📦 {pal || 0} pal{wkg ? ` · ${wkg.toLocaleString('fr-CA')} kg` : ''}
                </span>
              </>
            );
          })()}
          {false && stop.total_pieces != null && stop.total_pieces > 0 && (
            <>
              <span className="text-xs text-slate-500">•</span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold"
                title={`${stop.total_pieces} palettes${stop.weight_lbs ? ` · ${stop.weight_lbs} lbs` : ''}`}
                data-testid="stop-pallets-badge-legacy"
              >
                📦 {stop.total_pieces} pal
              </span>
            </>
          )}
          {rdvInPast && (
            <>
              <span className="text-xs text-slate-500">•</span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-300 border border-slate-600"
                title="Date de RDV dans le passé — ignorée par l'optimiseur (tri par géographie uniquement)"
              >
                🕰 RDV ignoré
              </span>
            </>
          )}
          {arrival && (
            <>
              <span className="text-xs text-slate-500">•</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${rdvOk === false ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                ETA {arrival}{rdvOk === false ? ' ⚠' : ''}
              </span>
            </>
          )}
        </div>
        <div className="text-sm font-semibold text-white truncate">{stop.client_name || '—'}</div>
        <div className="text-xs text-slate-400 truncate">
          {[stop.address, stop.city, stop.postal_code].filter(Boolean).join(', ')}
          {stop.required_date && (
            <span className={`ml-2 ${rdvInPast ? 'text-slate-600 line-through' : 'text-slate-500'}`}>
              • {stop.required_date} {stop.required_time || ''}
            </span>
          )}
        </div>
      </div>
      {editable && (
        <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="w-7 h-7 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 flex items-center justify-center disabled:opacity-30"
            data-testid={`move-up-${stop.id}`}
          >
            <ArrowUp size={13} />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="w-7 h-7 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 flex items-center justify-center disabled:opacity-30"
            data-testid={`move-down-${stop.id}`}
          >
            <ArrowDown size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// EXPANDED MAP MODAL (grande carte d'une option)
// ============================================================
const ExpandedMapModal = ({ option, onClose }) => {
  if (!option) return null;
  const miniStops = (option.stops_ordered || []).map((so) => ({
    id: so.id, lat: so.lat, lng: so.lng, label: so.label, stop_type: so.type,
  }));
  const modeLabels = { rapide: '⚡ Rapide', courte: '📏 Distance mini', economique: '💰 Économique' };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="expanded-map-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-white">{modeLabels[option.mode] || option.mode}</h2>
            <span className="text-xs text-slate-400">
              {option.total_distance_km?.toFixed?.(1)} km • {Math.round(option.total_duration_min)} min
              {option.fuel_litres != null && ` • ${option.fuel_litres.toFixed(1)} L`}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center"
            data-testid="expanded-map-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 p-4">
          <RouteMiniMap
            polylineCoords={option.polyline_coords || []}
            stops={miniStops}
            compact={false}
            height="100%"
          />
        </div>
        {/* Légende des arrêts */}
        <div className="border-t border-slate-800 px-5 py-3 max-h-40 overflow-y-auto shrink-0">
          <div className="flex flex-wrap gap-2">
            {(option.stops_ordered || []).map((s, idx) => {
              const isPickup = (s.type || '').toLowerCase() === 'pickup';
              return (
                <div
                  key={s.id || idx}
                  className={`text-[11px] px-2 py-1 rounded border ${isPickup ? 'bg-orange-500/10 border-orange-500/40 text-orange-200' : 'bg-green-500/10 border-green-500/40 text-green-200'}`}
                >
                  <strong>{idx + 1}</strong> · {isPickup ? '📦' : '🏁'} {s.label || s.address}
                  {s.arrival_estimate && <span className="ml-1 text-slate-400">({s.arrival_estimate})</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STOP DETAIL MODAL
// ============================================================
const StopDetailModal = ({ stop, onClose }) => {
  if (!stop) return null;
  const stopType = (stop.stop_type || stop.type || '').toLowerCase();
  const isPickup = stopType === 'cueillette' || stopType === 'pickup';
  const fullAddress = [stop.address, stop.city, stop.postal_code, stop.province].filter(Boolean).join(', ');
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="stop-detail-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg flex flex-col shadow-2xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPickup ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50' : 'bg-green-500/20 text-green-300 border border-green-500/50'}`}>
              {isPickup ? '📦' : '🏁'}
            </div>
            <div>
              <h2 className="text-sm font-bold text-white uppercase">{isPickup ? 'Cueillette' : 'Livraison'}</h2>
              <p className="text-xs text-slate-400">{stop.source_order_number || stop.order_number || '—'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center"
            data-testid="stop-detail-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto text-sm">
          <DetailRow icon={User} label="Client">{stop.client_name || '—'}</DetailRow>
          <DetailRow icon={MapPin} label="Adresse">{fullAddress || '—'}</DetailRow>
          {/* Sprint 2.7.5 — Checklist des bons agrégés pour le chauffeur */}
          {stop._merged_info && stop._merged_info.merged_count > 1 && (
            <div className="rounded-lg bg-purple-500/10 border border-purple-500/40 p-3" data-testid="merged-bons-checklist">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-bold text-purple-300 uppercase tracking-wide">
                  📋 {stop._merged_info.merged_count} bons à {isPickup ? 'ramasser' : 'livrer'} ici
                </span>
              </div>
              <div className="space-y-1.5">
                {(stop._merged_info.merged_bons || []).map((b, i) => (
                  <div
                    key={b.stop_id || i}
                    className="flex items-center gap-2 bg-slate-900/60 border border-slate-700/60 rounded px-2.5 py-1.5"
                    data-testid={`merged-bon-${i}`}
                  >
                    <input type="checkbox" className="w-4 h-4 accent-purple-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-white">
                        Bon {b.order_number || b.source_order_id?.slice(-6) || '—'}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {b.pallets ? `${b.pallets} pal` : '—'}
                        {b.weight_kg ? ` · ${b.weight_kg.toLocaleString('fr-CA')} kg` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-purple-500/20 text-[11px] text-purple-200">
                <strong>Total : {stop._merged_info.merged_total_pallets || 0} pal</strong>
                {stop._merged_info.merged_total_weight_kg
                  ? ` · ${stop._merged_info.merged_total_weight_kg.toLocaleString('fr-CA')} kg`
                  : ''}
              </div>
            </div>
          )}
          {stop.phone && <DetailRow icon={Phone} label="Téléphone">{stop.phone}</DetailRow>}
          {stop.contact_name && <DetailRow icon={User} label="Contact">{stop.contact_name}</DetailRow>}
          {stop.required_date && (
            <DetailRow icon={Clock} label="RDV requis">
              {stop.required_date} {stop.required_time ? `à ${stop.required_time}` : ''}
            </DetailRow>
          )}
          {stop.description && <DetailRow icon={FileText} label="Description">{stop.description}</DetailRow>}
          {stop.notes && <DetailRow icon={FileText} label="Notes">{stop.notes}</DetailRow>}
          {stop.quantity && <DetailRow icon={Package} label="Quantité">{stop.quantity}</DetailRow>}
          {stop.weight_kg && <DetailRow icon={Package} label="Poids">{stop.weight_kg} kg</DetailRow>}
          {stop._arrival && (
            <DetailRow icon={Clock} label="ETA calculé">
              <span className={stop._rdv_ok === false ? 'text-red-400' : 'text-emerald-400'}>
                {stop._arrival}{stop._rdv_ok === false ? ' (en retard sur RDV)' : ''}
              </span>
            </DetailRow>
          )}
        </div>
        {/* Lien vers Google Maps */}
        {fullAddress && (
          <div className="border-t border-slate-800 p-4 shrink-0">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300"
            >
              <MapPin size={13} /> Ouvrir dans Google Maps
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon size={16} className="text-slate-500 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-white break-words">{children}</div>
    </div>
  </div>
);

export default RouteOptimisationPage;
